import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import api, { formatApiError, setAccessToken } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { KeyRound, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();

  const [newPassword, setNewPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    // wipe any existing auth so user is forced to log in with new password
    setAccessToken(null);
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!token) {
      toast.error("Token mancante nel link");
      return;
    }
    if (newPassword !== confirm) {
      toast.error("Le due password non coincidono");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: newPassword });
      setDone(true);
      toast.success("Password reimpostata!");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#FDFBF7] px-4">
      <div className="w-full max-w-md bg-white border border-[#EAE3D9] rounded-3xl p-8 shadow-sm">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-xl bg-[#C8553D] text-[#FDFBF7] flex items-center justify-center">
            <KeyRound size={20} strokeWidth={1.8} />
          </div>
          <h1 className="font-display text-2xl font-bold text-[#2D2A26]">Reimposta password</h1>
        </div>
        <p className="text-sm text-[#757169] mb-6">
          Imposta una nuova password per il tuo account. Il link è valido per 1 ora.
        </p>

        {done ? (
          <div className="text-center py-6">
            <div className="w-12 h-12 mx-auto rounded-full bg-[#DDE7DC] text-[#2B593F] flex items-center justify-center mb-4">
              <CheckCircle2 size={24} />
            </div>
            <p className="font-medium text-[#2D2A26]">Password aggiornata.</p>
            <p className="text-sm text-[#757169] mt-1 mb-6">Ora puoi accedere con le nuove credenziali.</p>
            <Button
              data-testid="goto-login-button"
              onClick={() => navigate("/")}
              className="bg-[#2B593F] hover:bg-[#1E3F2C] text-[#FDFBF7] rounded-xl h-11 px-6 font-medium"
            >
              Vai al login
            </Button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-[0.1em] text-[#757169] font-medium">Nuova password</Label>
              <Input
                type="password"
                required
                minLength={8}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                data-testid="reset-new-password-input"
                placeholder="Almeno 8 caratteri"
                className="mt-2 bg-[#FDFBF7] border-[#EAE3D9] focus-visible:ring-[#2B593F]/30 focus-visible:border-[#2B593F] rounded-xl h-11"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.1em] text-[#757169] font-medium">Conferma password</Label>
              <Input
                type="password"
                required
                minLength={8}
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                data-testid="reset-confirm-password-input"
                className="mt-2 bg-[#FDFBF7] border-[#EAE3D9] focus-visible:ring-[#2B593F]/30 focus-visible:border-[#2B593F] rounded-xl h-11"
              />
            </div>
            <Button
              type="submit"
              disabled={busy}
              data-testid="reset-submit-button"
              className="w-full h-12 bg-[#2B593F] hover:bg-[#1E3F2C] text-[#FDFBF7] rounded-xl font-medium"
            >
              {busy ? "Aggiornamento..." : "Imposta nuova password"}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
